import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Wind, Volume2, Headphones } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ===============================
// AUDIO FUNCTIONS
// ===============================

// Create Web Audio API context
const createAudioContext = () => {
  return new (window.AudioContext || window.webkitAudioContext)();
};

// Play a simple tone
const playTone = (audioContext, frequency, duration, type = 'sine', volume = 0.3) => {
  if (!audioContext) return;
  
  try {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = type;
    
    gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  } catch (e) {
    console.log('Audio play error:', e);
  }
};

// Inhale sound - higher pitch tone (ding)
const playInhaleSound = (audioContext) => {
  playTone(audioContext, 440, 0.15, 'sine', 0.25); // La4
};

// Exhale sound - lower pitch tone (dang)
const playExhaleSound = (audioContext) => {
  playTone(audioContext, 330, 0.15, 'sine', 0.25); // Mi4
};

// Completion sound - ascending melody (dong)
const playCompleteSound = (audioContext) => {
  if (!audioContext) return;
  
  const notes = [523, 659, 784]; // Do5, Mi5, Sol5
  notes.forEach((freq, i) => {
    setTimeout(() => {
      playTone(audioContext, freq, 0.4, 'sine', 0.3);
    }, i * 200);
  });
};

export default function BreathingStage({ 
  totalBreaths = 40, 
  onComplete, 
  skipApnea = false  // For advanced sessions that don't have apnea
}) {
  const [currentBreath, setCurrentBreath] = useState(0);
  const [phase, setPhase] = useState('inhale'); // 'inhale', 'exhale'
  const [isComplete, setIsComplete] = useState(false);
  const [thetaVolume, setThetaVolume] = useState(0.5);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showHeadphonesTip, setShowHeadphonesTip] = useState(true);
  
  const thetaAudioRef = useRef(null);
  const audioContextRef = useRef(null);
  const breathIntervalRef = useRef(null);
  const inhaleTime = 2000; // 2 seconds
  const exhaleTime = 2000; // 2 seconds

  // Initialize audio context and theta audio
  useEffect(() => {
    // Create audio context for breath sounds
    audioContextRef.current = createAudioContext();
    
    // Start theta binaural audio
    if (thetaAudioRef.current) {
      thetaAudioRef.current.volume = thetaVolume;
      thetaAudioRef.current.loop = true;
      thetaAudioRef.current.play().catch(() => {});
    }
    
    return () => {
      if (thetaAudioRef.current) {
        thetaAudioRef.current.pause();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  // Update theta volume
  useEffect(() => {
    if (thetaAudioRef.current) {
      thetaAudioRef.current.volume = thetaVolume;
    }
  }, [thetaVolume]);

  // Breathing cycle
  useEffect(() => {
    if (isComplete) return;

    // Play breath sound at the start of each phase (if enabled)
    if (soundEnabled) {
      if (phase === 'inhale') {
        playInhaleSound(audioContextRef.current);
      } else {
        playExhaleSound(audioContextRef.current);
      }
    }

    breathIntervalRef.current = setTimeout(() => {
      if (phase === 'inhale') {
        setPhase('exhale');
      } else {
        // Complete breath
        const newBreath = currentBreath + 1;
        setCurrentBreath(newBreath);
        
        if (newBreath >= totalBreaths) {
          setIsComplete(true);
          // Completion sound ALWAYS plays
          playCompleteSound(audioContextRef.current);
          
          setTimeout(() => {
            onComplete?.();
          }, 2000);
        } else {
          setPhase('inhale');
        }
      }
    }, phase === 'inhale' ? inhaleTime : exhaleTime);

    return () => {
      if (breathIntervalRef.current) {
        clearTimeout(breathIntervalRef.current);
      }
    };
  }, [phase, currentBreath, isComplete, totalBreaths, soundEnabled, skipApnea]);

  const progress = (currentBreath / totalBreaths) * 100;

  if (isComplete) {
    return (
      <div className="flex flex-col items-center justify-center py-10" data-testid="breathing-complete">
        <audio ref={thetaAudioRef} src={`${API_URL}/api/audio/theta_loop.mp3`} />
        
        <motion.div
          className="w-56 h-56 md:w-72 md:h-72 rounded-full bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-400/30 flex items-center justify-center"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
        >
          <div className="text-center">
            <p className="text-4xl mb-3">🌟</p>
            <p className="text-xl font-medium text-white mb-2">¡Respiración completa!</p>
            <p className="text-sm text-white/60">{totalBreaths} ciclos</p>
          </div>
        </motion.div>
        
        <p className="mt-6 text-emerald-400 font-medium">
          {skipApnea ? 'Preparándote para las imágenes...' : 'Prepárate para retener el aire...'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-6" data-testid="breathing-stage">
      {/* Hidden theta audio */}
      <audio ref={thetaAudioRef} src={`${API_URL}/api/audio/theta_loop.mp3`} />
      
      {/* Headphones tip */}
      {showHeadphonesTip && (
        <motion.div 
          className="mb-4 p-3 bg-[hsl(252,37%,60%)]/10 border border-[hsl(252,37%,60%)]/30 rounded-lg flex items-center gap-2 max-w-md"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Headphones className="w-5 h-5 text-[hsl(252,37%,60%)] flex-shrink-0" />
          <p className="text-sm text-white/70">
            Usa <strong className="text-white">auriculares</strong> para el efecto binaural theta
          </p>
          <button 
            onClick={() => setShowHeadphonesTip(false)}
            className="text-white/40 hover:text-white ml-2"
          >
            ✕
          </button>
        </motion.div>
      )}
      
      {/* Main breathing circle */}
      <motion.div
        className={`relative w-56 h-56 md:w-72 md:h-72 rounded-full flex items-center justify-center ${
          phase === 'inhale' 
            ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-2 border-cyan-400/40' 
            : 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-400/40'
        }`}
        animate={{ 
          scale: phase === 'inhale' ? [1, 1.15] : [1.15, 1],
        }}
        transition={{ duration: phase === 'inhale' ? inhaleTime / 1000 : exhaleTime / 1000, ease: 'easeInOut' }}
      >
        <div className="text-center z-10">
          <Wind className={`w-10 h-10 mx-auto mb-2 ${
            phase === 'inhale' ? 'text-cyan-400' : 'text-purple-400'
          }`} />
          <p className={`text-2xl font-bold uppercase tracking-wider ${
            phase === 'inhale' ? 'text-cyan-400' : 'text-purple-400'
          }`}>
            {phase === 'inhale' ? 'Inhala' : 'Exhala'}
          </p>
          <p className="text-white/50 text-sm mt-2">
            {currentBreath + 1} / {totalBreaths}
          </p>
        </div>
      </motion.div>

      {/* Progress bar */}
      <div className="w-full max-w-xs mt-8">
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-cyan-500 to-purple-500"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <p className="text-center text-white/50 text-sm mt-2">
          {Math.round(progress)}% completado
        </p>
      </div>

      {/* Audio controls */}
      <div className="mt-6 flex items-center gap-4">
        {/* Theta volume */}
        <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
          <Volume2 className="w-4 h-4 text-[hsl(252,37%,60%)]" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={thetaVolume}
            onChange={(e) => setThetaVolume(parseFloat(e.target.value))}
            className="w-20 h-1 bg-white/20 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-3
              [&::-webkit-slider-thumb]:h-3
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-[hsl(252,37%,60%)]"
          />
          <span className="text-xs text-white/40 w-8">{Math.round(thetaVolume * 100)}%</span>
        </div>

        {/* Sound toggle */}
        <button
          onClick={() => setSoundEnabled(!soundEnabled)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            soundEnabled 
              ? 'bg-[hsl(198,62%,54%)]/20 text-[hsl(198,62%,54%)]' 
              : 'bg-white/5 text-white/40'
          }`}
          data-testid="sound-toggle"
        >
          <Volume2 className="w-4 h-4" />
          <span className="text-xs">{soundEnabled ? 'Sonido ON' : 'Sonido OFF'}</span>
        </button>
      </div>
    </div>
  );
}
